import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { BLOG_POSTS, formatDate } from "@/data/blogPosts";

export default function Blog() {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    document.title = "Pitcrew Blog — Car Care Tips, Diagnostics & Repair Guides";
    const meta = document.querySelector('meta[name="description"]');
    if (meta)
      meta.setAttribute(
        "content",
        "Practical car care guides, diagnostic tips, and repair advice from Pitcrew — plus free instant AI diagnosis for your vehicle."
      );
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200" data-testid="blog-page">
      {/* Nav */}
      <header className="sticky top-0 z-30 h-16 border-b border-slate-800/60 bg-slate-950/80 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto h-full px-6 flex items-center gap-3">
          <img
            src="/pitcrew-logo-horizontal.png"
            alt="Pitcrew"
            data-testid="blog-logo"
            className="h-9 md:h-11 w-auto cursor-pointer"
            onClick={() => navigate("/")}
          />
          <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
            <Button
              variant="ghost"
              size="sm"
              data-testid="blog-nav-home"
              onClick={() => navigate("/")}
              className="px-2.5 sm:px-3 text-slate-300 hover:text-white hover:bg-slate-800"
            >
              Home
            </Button>
            <Button
              data-testid="blog-nav-diagnosis"
              onClick={() => navigate("/")}
              className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
            >
              {user ? "Go to Dashboard" : "Get Instant AI Diagnosis"}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-slate-800/60">
        <div className="max-w-4xl mx-auto px-6 py-16 sm:py-20">
          <p className="text-amber-500 font-head font-semibold tracking-widest uppercase text-xs mb-3">
            The Pitcrew Blog
          </p>
          <h1 className="font-head font-black text-4xl sm:text-5xl text-slate-50 leading-tight max-w-3xl">
            Car care, decoded.
          </h1>
          <p className="text-slate-400 mt-4 max-w-xl text-base">
            Straight-talking guides on diagnostics, repairs, and getting a fair price — so you
            always know what your car is really telling you.
          </p>
        </div>
      </section>

      {/* Post grid */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {BLOG_POSTS.map((post) => (
            <article
              key={post.slug}
              data-testid={`blog-card-${post.slug}`}
              onClick={() => navigate(`/blog/${post.slug}`)}
              className="group cursor-pointer rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden hover:border-amber-500/50 transition-colors flex flex-col"
            >
              <div className="aspect-[16/10] overflow-hidden bg-slate-900">
                <img
                  src={post.image}
                  alt={post.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                  <span className="px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 font-semibold">
                    {post.category}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {post.readTime}
                  </span>
                </div>
                <h2 className="font-head font-bold text-lg text-slate-50 leading-snug group-hover:text-amber-400 transition-colors">
                  {post.title}
                </h2>
                <p className="text-slate-400 text-sm mt-3 leading-relaxed flex-1">{post.excerpt}</p>
                <div className="mt-5 flex items-center justify-between text-sm">
                  <span className="text-slate-500">{formatDate(post.date)}</span>
                  <span className="inline-flex items-center gap-1 text-amber-400 font-semibold">
                    Read <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center gap-3 justify-between text-sm text-slate-500">
          <img src="/pitcrew-logo-horizontal.png" alt="Pitcrew" className="h-7 object-contain" />
          <p>© {new Date().getFullYear()} · Professional auto repair & service</p>
          <button
            data-testid="blog-footer-home"
            onClick={() => navigate("/")}
            className="hover:text-slate-300"
          >
            Back to home
          </button>
        </div>
      </footer>
    </div>
  );
}
